import { Injectable } from '@angular/core';
import { Labor } from '../interface/labor';
import { Part } from '../interface/part';
import { RelatedItem } from '../interface/related-item';
import { RoLaborFull } from '../interface/ro-labor-full';
import { RoLaborService } from '../service/ro-labor.service';
import { RoPartService } from '../service/ro-part.service';
import { SnackbarService } from '../service/snackbar.service';
import { partStates } from '../shared/lists/part-states';
import { RoStateGroupPipe } from '../shared/pipes/ro-state-group.pipe';
import { StockController } from './stock.controller';
import { RoService } from '../service/ro.service';
import { MkgOS } from '../class/mkg-os';
import { MkgOSPart, MkgOSPartApiOptions } from '../class/mkg-os-part';
import { IbptImportService } from '../service/ibpt-import.service';
import { PartService } from '../service/part.service';
import { LaborService } from '../service/labor.service';
import { OperationService } from '../service/operation.service';
import { DataService } from '../service/data.service';
import { BOOLEAN_TWO } from '../shared/common-enums/boolean-two';
import { MoveTypes } from '../shared/lists/move-type';
import { OperationFunction } from '../shared/lists/operation-functions';
import { FieldChanged } from '../shared/type-aliases/field-changed';
import { companyStates } from '../shared/lists/company-states';
import { LOG_REPORTER_KEYS, LogReporterService } from '../service/log-reporter.service';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

interface RelatedRequest {
  part: MkgOSPart[],
  labor: RoLaborFull[]
}

@Injectable({
  providedIn: 'root'
})
export class RelatedController {

  constructor(
    private roPartService: RoPartService,
    private _dataService: DataService,
    private roLaborService: RoLaborService,
    private snackbar: SnackbarService,
    private _partsService: PartService,
    private _laborsService: LaborService,
    private stockController: StockController,
    private roStateGroupPipe: RoStateGroupPipe,
    private _roService: RoService,
    private _ibptImportService: IbptImportService,
    private _logReporter: LogReporterService,
    private _operationService: OperationService
  ) { }

  public async onPartAddedToRO({
    additional,
    movementCaller,
    part,
    ro,
    amount,
    mechanic,
    status,
    osLabor,
    apiOptions
  }: { ro: MkgOS, part: Part, additional: boolean, movementCaller: string, mechanic?: string, amount?: number, status?: number, osLabor?: string, apiOptions?: MkgOSPartApiOptions }) {
    let ncmIbpt: any;

    if (this._dataService.company.currentState !== companyStates.integrated.id) {
      ncmIbpt = await this._getNCMData(part);
    }


    /** The item related parts and labors to add to RO */
    let req: RelatedRequest = {
      part: [],
      labor: []
    };

    /** The main part selected by user to add to RO */
    const main = new MkgOSPart({
      part: part,
      amount: amount ?? 1,
      purchaseValue: part.cost * 100,
      purchaseAverage: part.averageCost * 100,
      available: status ?? this._getAvailableStateToAdd(additional),
      saleValue: part.saleValue * 100,
      seq: ro.parts.length + 1,
      osLabor
    });

    if (mechanic) {
      main.mechanic = mechanic;
    }

    if (ncmIbpt) {
      main["nationalFederal"] = ncmIbpt.nationalFederal;
      main["state"] = ncmIbpt.state;
      main["municipal"] = ncmIbpt.municipal;
      main["key"] = ncmIbpt.key;
    }
    req.part.push(main);

    /** The related "child" items to add/update in RO */
    for (const idx in part.itemRelated) {
      let related = part.itemRelated[idx];
      const roItemRelated = await this._assemblyRelated(main, related, ro, additional);
      if (!roItemRelated) {
        continue;
      }

      if (roItemRelated instanceof MkgOSPart) {
        (roItemRelated as MkgOSPart).seq = ro.parts.length + Number(idx) + 2;
        // Onda2·E3·T4, decisão 15: peças relacionadas (kit) vão para o mesmo serviço do "+".
        // Os serviços do kit (RoLaborFull) entram sem osLabor — não é campo deles.
        if (osLabor) {
          (roItemRelated as MkgOSPart).osLabor = osLabor;
        }
        req.part.push(roItemRelated);
      } else {
        req.labor.push(roItemRelated);
      }
    }
    let operation = ro.operation;


    if (['opened', 'closed'].includes(this.roStateGroupPipe.transform(ro.currentState)) && operation && operation.changeStock) {
      for (const roPartFull of req.part) {
        await this.changePartStock(roPartFull, {
          caller: movementCaller + "; RelatedController.onPartAddedToRO()",
          moveType: MoveTypes.amountAdded,
          OS: ro,
          operationFunction: "Adicionada à OS",
          fieldChanged: "amount",
        });
      }
    }

    await this.updateRO(ro, req, 'add', apiOptions);
    if (part.itemRelated?.length) {
      this.changedMessage();
    }
  }

  private async _getNCMData(part: Part) {
    let ncm: any;
    try {
      if (this._dataService.company.currentState !== companyStates.integrated.id) {
        ncm = await this._ibptImportService.getIbptData(part.NCM);
      }
    } catch (error) {
      console.error(error);
      console.warn(`NCM da peça não encontrada ${part}`);
      if (part.NCM <= 9 || part.NCM === undefined || part.NCM === null) {
        this.snackbar.error("NCM.NOT_FOUND", { ncm: part.NCM });
      }
    } finally {
      return ncm;
    }
  }

  private _getAvailableStateToAdd(additional: boolean): number {
    if (this._dataService.company.complementaryOS === BOOLEAN_TWO.TRUE) {
      return additional ? partStates.additional.id : partStates.waiting.id;
    } else {
      return additional ? partStates.approved.id : partStates.waiting.id;
    };
  }

  public async onUpdatedPart(ro: MkgOS, roPartFull: MkgOSPart, movementCaller: string, fieldChanged: FieldChanged, apiOptions?: MkgOSPartApiOptions) {
    let req: RelatedRequest = {
      part: [],
      labor: []
    };

    req.part.push(roPartFull);

    // ✅ CORREÇÃO: Apenas buscar itens relacionados se não for mudança de desconto
    // Mudanças de desconto afetam apenas a peça alterada - backend recalcula os descontos proporcionais
    const shouldUpdateRelated = !['additionalDiscount', 'discountValue', 'saleValue'].includes(fieldChanged);

    // get related items to update
    if (roPartFull.part && shouldUpdateRelated) {
      roPartFull.part.itemRelated = await this.getRelatedsOfPart(roPartFull.part.id);
      // The related "child" items to add to RO
      if (roPartFull.part.itemRelated) {
        for (const related of roPartFull.part.itemRelated) {
          const itemAlreadyApplyed = ro.parts.find(part => related.item === part.part.id);
          const roItemRelated = await this._assemblyRelated(roPartFull, related, ro as any, null, true, itemAlreadyApplyed ? itemAlreadyApplyed.oldAmount ?? itemAlreadyApplyed.amount() : undefined);

          if (roItemRelated instanceof MkgOSPart) {
            roItemRelated.delivery = itemAlreadyApplyed?.delivery ?? 1;
            req.part.push(roItemRelated);
          } else {
            req.labor.push(roItemRelated);
          }
        }
      }
    }

    let operation = ro.operation;

    if (['opened', 'closed'].includes(this.roStateGroupPipe.transform(ro.currentState)) && operation && operation.changeStock) {
      for (const roPartFull of req.part) {
        const adding = roPartFull.amount() > roPartFull.oldAmount;
        let operationFunctionFromPart: OperationFunction = adding ? "Qntd. Adicionada à OS" : "Qntd. Removida da OS";

        if (roPartFull.amount() === 0) {
          operationFunctionFromPart = "Removida da OS";
        }

        if (roPartFull.amount() !== roPartFull.oldAmount) {
          await this.changePartStock(roPartFull, {
            caller: movementCaller + "; RelatedController.onUpdatedPart()",
            moveType: adding ? MoveTypes.amountAdded : MoveTypes.amountSubtracted,
            OS: ro,
            operationFunction: operationFunctionFromPart,
            fieldChanged
          });
        }
      }
    }

    await this.updateRO(ro, req, 'amount', apiOptions);
    if (roPartFull.part.itemRelated?.length) {
      this.changedMessage();
    }
  }

  private async getParentsOf(relatedId: string, ro: MkgOS): Promise<MkgOSPart[]> {
    let parents: MkgOSPart[] = [];
    for (const roPart of ro.parts) {
      // ✅ Otimização: Só busca itemRelated se ainda não estiver carregado
      if (!roPart.part.itemRelated) {
        roPart.part.itemRelated = await this.getRelatedsOfPart(roPart.part.id);
      }
      if (roPart.part.itemRelated) {
        const include = roPart.part.itemRelated?.some(related => related.item === relatedId);
        if (include) {
          parents.push(roPart);
        }
      }
    }
    return parents;
  }

  public async onPartRemovedFromRO(roPartFull: MkgOSPart, ro: MkgOS, movementCaller: string) {
    let req: RelatedRequest = {
      part: [],
      labor: []
    };
    req.part.push(roPartFull);

    // ✅ get related items - só busca se ainda não estiver carregado
    if (!roPartFull.part.itemRelated) {
      roPartFull.part.itemRelated = await this.getRelatedsOfPart(roPartFull.part.id);
    }

    // remove related "parents"
    const parents = await this.getParentsOf(roPartFull.part.id, ro);
    for (let parent of parents) {
      // ✅ Usa itemRelated já carregado pelo getParentsOf
      const relateds = parent.part.itemRelated;
      if (relateds?.length) {
        for (let related of relateds) {
          if (related.item === roPartFull.part.id && related.removeMain) {
            req.part.push(parent);
          }
        }
      }
    }

    // The related "child" items to remove from RO
    if (roPartFull.part.itemRelated) {
      for (const related of roPartFull.part.itemRelated) {
        const itemAlreadyApplyed = ro.parts.find(part => related.item === part.part.id);
        const roItemRelated = await this._assemblyRelated(roPartFull, related, ro, null, true, itemAlreadyApplyed ? itemAlreadyApplyed.oldAmount ?? itemAlreadyApplyed.amount() : undefined);
        if (related.removeMainAll) {
          // remove child relateds
          if (roItemRelated instanceof MkgOSPart) {
            req.part.push(roItemRelated);
          } else {
            req.labor.push(roItemRelated);
          }
        }
      }
    }

    let operation = ro.operation;

    if (['opened', 'closed'].includes(this.roStateGroupPipe.transform(ro.currentState)) && operation && operation.changeStock) {
      for (const roPartFull of req.part) {
        await this.changePartStock(roPartFull, {
          caller: movementCaller + "; RelatedController.onPartRemovedFromRO()",
          OS: ro,
          moveType: MoveTypes.amountSubtracted,
          operationFunction: "Qntd. Removida da OS",
          fieldChanged: "amount",
        }, true);
      }
    }

    await this.deleteItemsFromRO(ro.id, req);
    if (roPartFull.part.itemRelated?.length) {
      this.changedMessage();
    }
  }

  // change stock
  private async changePartStock(roPart: MkgOSPart, movementData: {
    OS: MkgOS;
    caller: string;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    fieldChanged: FieldChanged;
  }, isDelete?: boolean,) {
    const { OS, caller, moveType, operationFunction, fieldChanged } = movementData;

    const textLogger = this._logReporter.textLogger(caller + "RelatedController.changePartStock()" + "fieldChanged: " + fieldChanged, LOG_REPORTER_KEYS.STOCK);

    if (isDelete) {
      roPart.oldAmount = roPart.amount();
      roPart.amount.set(0);
    }

    const logInfo = {
      isDelete,
      oldAmount: roPart.oldAmount,
      amount: roPart.amount(),
      fieldChanged,
      "roPart.available": roPart.available,
      moveType,
      osId: OS.id,
      partId: roPart.part.id,
    }

    textLogger(JSON.stringify(logInfo));

    const dayToCompareAndPreventStockErrors = moment("2025-04-01T03:00:00.000Z").startOf("day");

    if ((roPart.available === partStates.approved.id) || (roPart.available === partStates.additional.id && (roPart.createdAt && roPart.createdAt.isBefore(dayToCompareAndPreventStockErrors, "day")))) {
      await this.stockController.onPartUpdatedIntoOS(roPart, roPart.amount() - (roPart.oldAmount || 0), {
        caller: caller + "; RelatedController.changePartStock()",
        moveType,
        OS,
        operationFunction: isDelete ? "Removida da OS" : operationFunction,
        fieldChanged
      });
    }
  }

  public async onLaborRemovedFromRO(roLaborFull: RoLaborFull, ro: MkgOS) {
    let req: RelatedRequest = {
      part: [],
      labor: []
    };
    req.labor.push(roLaborFull);

    // remove related "parents"
    const parents = await this.getParentsOf(roLaborFull.labor.id, ro);
    for (let parent of parents) {
      const relateds = await this.getRelatedsOfPart(parent.part.id);
      for (let related of relateds) {
        if (related.item === roLaborFull.labor.id && related.removeMain) {
          req.part.push(parent);
        }
      }
    }

    let operation = ro.operation;

    if (['opened', 'closed'].includes(this.roStateGroupPipe.transform(ro.currentState)) && operation && operation.changeStock) {
      for (const roPartFull of req.part) {
        await this.changePartStock(roPartFull, {
          caller: "RoDetailComponent.deleteRoLabor()" + "; RelatedController.onPartRemovedFromRO()",
          moveType: MoveTypes.amountSubtracted,
          OS: ro,
          operationFunction: "Qntd. Removida da OS",
          fieldChanged: 'amount',
        }, true);
      }
    }

    await this.deleteItemsFromRO(ro.id, req);
    roLaborFull.amount = 0;
    this.changedMessage();
  }

  private getIdItemInRO(ro: MkgOS, related: RelatedItem, type: "part" | "labor"): string {
    if (type == 'part') {
      const item = ro.parts.find(roPart => roPart.part.id === related.item);
      return (item ? item.id : undefined);
    }
    if (type == 'labor') {
      const item = ro.labors.find(roLabor => roLabor.labor.id === related.item);
      return (item ? item.id : undefined);
    }
  }

  private async getRelatedsOfPart(id: string): Promise<Part['itemRelated'] | undefined> {
    const part = await this._partsService.get(id);
    return part?.itemRelated;
  }

  private async _assemblyRelated(main: MkgOSPart, related: RelatedItem, ro: MkgOS, additional?: boolean, isEditingMainRelated = false, oldAmount?: number): Promise<MkgOSPart | RoLaborFull> {
    let roItem: Partial<RoLaborFull> = {
      available: isEditingMainRelated ? main.available : this._getAvailableStateToAdd(additional),
      amount: related.sameAmount ? main.amount() : related.fixedAmount && !related.amount ? related.fixedAmount : (related.amount * main.amount())
    };
    let part: Part;
    let labor: Labor;

    let type: "part" | "labor" | undefined;

    try {
      part = await this._partsService.get(related.item);
      if (part.available === 0) {
        return;
      }

      type = 'part';
    } catch {
      // don't worries
    }

    if (!part) {
      try {
        labor = await this._laborsService.get(related.item);
        type = "labor";
      } catch {
        // don't worries
      }
    }

    // avoid duplicate an item into ro
    if (ro) {
      const id = this.getIdItemInRO(ro, related, type);
      if (id) {
        roItem.id = id;
      }
    }

    switch (type) {
      case "part":
        let ncm;

        if (this._dataService.company.currentState !== companyStates.integrated.id) {
          ncm = await this._getNCMData(part);
        }

        return new MkgOSPart({
          id: roItem['id'],
          amount: roItem.amount,
          available: roItem.available,
          part,
          saleValue: related.main ? main.saleValue() : part.saleValue,
          purchaseValue: part.cost,
          purchaseAverage: part.averageCost,
          oldAmount: oldAmount ?? main.oldAmount,
          nationalFederal: ncm?.nationalFederal,
          state: ncm?.state,
          municipal: ncm?.municipal,
          key: ncm?.key
        }, false);
      case "labor":
        roItem["labor"] = labor;
        roItem["saleValue"] = labor.saleValue;
        break;
      default:
        throw new Error("Item relacionado não encontrado");
    }
    if (related.main) {
      roItem.saleValue = main.saleValue();
    }
    roItem.liquidValue = roItem.saleValue * roItem.amount;
    return roItem;
  }


  private async updateRO(ro: MkgOS, related: RelatedRequest, from: "amount" | 'add', apiOptions?: MkgOSPartApiOptions) {
    let requests = [];
    if (from === 'amount') {
      related.part.filter(part => !!part).forEach(roPartFull => {
        if (roPartFull.id) {
          requests.push(this.roPartService.addOrUpdate(ro.id, roPartFull, apiOptions));
        }
      });
      related.labor.filter(labor => !!labor).forEach(roLaborFull => {
        if (roLaborFull.id) {
          requests.push(this.roLaborService.addOrUpdate(ro.id, roLaborFull));
        }
      });
    } else {
      related.part.filter(part => !!part).forEach(roPartFull => {
        requests.push(this.roPartService.addOrUpdate(ro.id, roPartFull, apiOptions));
      });
      related.labor.filter(labor => !!labor).forEach(roLaborFull => {
        requests.push(this.roLaborService.addOrUpdate(ro.id, roLaborFull));
      });
    }

    await Promise.all(requests);
  }

  private async deleteItemsFromRO(roId: string, related: RelatedRequest) {
    let requests = [];
    related.part.forEach(roPartFull => {
      if (roPartFull.id) {
        requests.push(this.roPartService.delete(roId, roPartFull.id));
      }
    });
    related.labor.forEach(roLaborFull => {
      if (roLaborFull.id) {
        requests.push(this.roLaborService.delete(roId, roLaborFull.id));
      }
    })
    await Promise.all(requests);
  }

  private changedMessage() {
    setTimeout(() => {
      this.snackbar.success('BUDGET.RELATED_CHANGED');
    }, 2500);
  }

  /** Redistribute the seq in each part of a OS */
  public async resetSeqOf(osParts: MkgOSPart[], osId: string) {
    const orderedParts = osParts.sort((a, b) => {
      const seqA = Number.isFinite(a.seq) ? a.seq : Number.MAX_SAFE_INTEGER;
      const seqB = Number.isFinite(b.seq) ? b.seq : Number.MAX_SAFE_INTEGER;
      return seqA - seqB;
    });
    const needsUpdate = orderedParts.some((roPart, index) => roPart.seq !== index + 1);
    if (!needsUpdate) {
      return;
    }

    const requests = orderedParts.flatMap((roPart, index) => {
      const seq = index + 1;
      if (roPart.seq === seq) {
        return [];
      }

      roPart.seq = seq;
      return [firstValueFrom(this.roPartService.updateFields(osId, roPart.id, { seq }))];
    });

    await Promise.all(requests);
  }

}
